import { useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { TriangleAlert } from "lucide-react";

import { cn } from "@/lib/utils";

import { DirectorTimelineLanes } from "./DirectorTimelineLanes";
import { TranscriptPanel } from "./TranscriptPanel";
import { NARRATION_TIER_LABEL } from "./narrationLabels";
import type { NarrationLine } from "./narrationTimeline";
import type { SyncTimeline } from "./syncTimeline";
import type { DirectorNarrationState } from "./useDirectorNarration";

// ---------------------------------------------------------------------------
// DirectorNarrationContent — the narration read-out itself (chips, ramp
// warning, lanes, transcript), shared by the inline card and the fullscreen
// overlay so the two can never drift apart (DIR-013).
//
// `tone` exists because fullscreen paints on black: the muted foreground token
// that reads well on a card is nearly invisible there.
// ---------------------------------------------------------------------------

type NarrationTone = "surface" | "dark";

/** Below this the "max lead" chip is noise rather than a finding. */
const LEAD_CHIP_THRESHOLD_SECONDS = 0.75;

export function NarrationSummaryBadges({
  state,
  tone = "surface",
}: {
  state: DirectorNarrationState;
  tone?: NarrationTone;
}) {
  const { narration, drift } = state;
  const summary = drift?.summary;
  const outline = tone === "dark" ? "border-white/30 text-white" : "";

  return (
    <>
      {narration && (
        <Badge variant="outline" className={cn("text-[10px]", outline)}>
          {NARRATION_TIER_LABEL[narration.source]}
        </Badge>
      )}
      {summary?.meanCoverageRatio != null && (
        <Badge
          variant="outline"
          className={cn(
            "text-[10px]",
            outline,
            summary.severity === "critical" && "border-destructive",
            summary.severity === "warn" && "border-amber-500",
          )}
          title="Share of each line that plays over the scene it was written for"
        >
          sync {Math.round(summary.meanCoverageRatio * 100)}%
        </Badge>
      )}
      {summary?.maxLeadSeconds != null &&
        summary.maxLeadSeconds >= LEAD_CHIP_THRESHOLD_SECONDS && (
          <Badge
            variant="outline"
            className={cn("border-amber-500 text-[10px]", outline)}
            title="Worst case of narration running ahead of its images"
          >
            {summary.estimated ? "~" : ""}max lead{" "}
            {summary.maxLeadSeconds.toFixed(1)}s
          </Badge>
        )}
      {summary?.orphanedLineCount ? (
        <Badge variant="destructive" className="text-[10px]">
          {summary.orphanedLineCount} orphaned
        </Badge>
      ) : null}
    </>
  );
}

export function DirectorNarrationContent({
  state,
  sync,
  video,
  durationSeconds,
  onSeekSeconds,
  onSelectScene,
  isAdmin,
  tone = "surface",
}: {
  state: DirectorNarrationState;
  sync: SyncTimeline;
  video: HTMLVideoElement | null;
  durationSeconds: number | null;
  onSeekSeconds: (seconds: number) => void;
  onSelectScene?: (sceneIndex: number) => void;
  isAdmin?: boolean;
  tone?: NarrationTone;
}) {
  const { narration, drift, peaks, activeLineIndex, beats, cutTimes } = state;

  const seekToLine = useCallback(
    (line: NarrationLine) => onSeekSeconds(line.startSeconds),
    [onSeekSeconds],
  );

  if (!narration || !drift) return null;

  const summary = drift.summary;
  const mutedText = tone === "dark" ? "text-white/70" : "text-muted-foreground";

  return (
    <div className="space-y-3">
      {summary.rampDetected && (
        <div
          className={cn(
            "flex items-start gap-2 rounded border px-3 py-2 text-xs",
            tone === "dark"
              ? "border-red-400/50 bg-red-500/15 text-red-200"
              : "border-destructive/40 bg-destructive/10 text-destructive",
          )}
        >
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Scene starts drift steadily later from scene{" "}
            {summary.rampStartSceneIndex}, accumulating{" "}
            {summary.rampTotalSeconds?.toFixed(1)}s. That is the signature of a
            beat-snap retime: narration keeps its written offsets while every
            scene stretches, so lines increasingly play before the images they
            describe.
          </span>
        </div>
      )}

      {peaks.status === "loading" && (
        <p className={cn("text-xs", mutedText)}>
          Decoding the delivered audio…
        </p>
      )}
      {(peaks.status === "error" || peaks.status === "unsupported") && (
        <p className={cn("text-xs", mutedText)}>
          Waveform unavailable ({peaks.error ?? "unsupported"}) — the narration
          blocks and transcript below still work.
        </p>
      )}

      <DirectorTimelineLanes
        sync={sync}
        narration={narration}
        drift={drift}
        peaks={peaks.peaks}
        video={video}
        durationSeconds={durationSeconds ?? peaks.durationSeconds ?? 0}
        onSeek={onSeekSeconds}
        activeLineIndex={activeLineIndex}
        showBeats={isAdmin && beats.length > 0}
        beatTimes={beats}
        cutTimes={cutTimes}
      />

      <TranscriptPanel
        narration={narration}
        drift={drift}
        activeLineIndex={activeLineIndex}
        onSeekToLine={seekToLine}
        onSelectScene={onSelectScene}
        isAdmin={isAdmin}
        tone={tone}
      />

      {narration.warnings.length > 0 && isAdmin && (
        <p className={cn("text-[11px]", mutedText)}>
          resolver notes: {narration.warnings.join(", ")}
        </p>
      )}
    </div>
  );
}
